const KEY_MUTED = 'lavascend.muted';

class LavaAudio {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicEl: HTMLAudioElement | null = null;
  private muted = localStorage.getItem(KEY_MUTED) === '1';

  init(): void {
    if (this.ctx) return;
    const Ctx: typeof AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.muted ? 0 : 1;
    this.masterGain.connect(this.ctx.destination);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.masterGain) this.masterGain.gain.value = m ? 0 : 1;
  }

  music(): void {
    if (!this.ctx || !this.masterGain || this.musicEl) return;
    const el = new Audio('/audio/bg-music.mp3');
    el.loop = true;
    el.preload = 'auto';
    const src = this.ctx.createMediaElementSource(el);
    const musicGain = this.ctx.createGain();
    musicGain.gain.value = 0.6;
    src.connect(musicGain).connect(this.masterGain);
    el.play().catch(() => { /* user gesture already happened; play() should succeed, but swallow any racy AbortError */ });
    this.musicEl = el;
  }

  // -----------------------------------------------------------------
  // SFX vocabulary — see docs/superpowers/specs/2026-05-15-audio-pass-design.md.
  // Each method is a placeholder synth. Replacing one with a real asset is a
  // single-method edit (load /audio/sfx/<name>.mp3 once, route through this.masterGain).
  // -----------------------------------------------------------------

  uiTap(): void {
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator(); osc.type = 'sine';
    osc.frequency.setValueAtTime(660, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.06);
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.25, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.connect(g); g.connect(dest);
    osc.start(now); osc.stop(now + 0.15);
  }

  start(): void {
    // Rising tonic chord — kept identical to former `swell` so the play-tap feel
    // is unchanged when no real asset has been provided yet.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const fundamental = 220;
    const ratios = [1, 1.25, 1.5, 1.875];
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.25, now + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
    g.connect(dest);
    for (const r of ratios) {
      const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.value = fundamental * r;
      o.detune.value = (Math.random() - 0.5) * 10;
      o.connect(g); o.start(now); o.stop(now + 1.2);
    }
  }

  panelOpen(): void {
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator(); osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.25);
    const filt = ctx.createBiquadFilter(); filt.type = 'lowpass';
    filt.frequency.setValueAtTime(600, now);
    filt.frequency.exponentialRampToValueAtTime(4000, now + 0.25);
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.18, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    osc.connect(filt); filt.connect(g); g.connect(dest);
    osc.start(now); osc.stop(now + 0.3);
  }

  panelClose(): void {
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator(); osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(220, now + 0.2);
    const filt = ctx.createBiquadFilter(); filt.type = 'lowpass';
    filt.frequency.setValueAtTime(4000, now);
    filt.frequency.exponentialRampToValueAtTime(500, now + 0.2);
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.16, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.connect(filt); filt.connect(g); g.connect(dest);
    osc.start(now); osc.stop(now + 0.25);
  }

  lever(): void {
    // Mechanical thunk — kept identical to former `thunk`.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator(); osc.type = 'sine'; osc.frequency.value = 110;
    const filt = ctx.createBiquadFilter(); filt.type = 'lowpass';
    filt.frequency.setValueAtTime(800, now);
    filt.frequency.exponentialRampToValueAtTime(120, now + 0.18);
    const g = ctx.createGain(); g.gain.value = 0.4;
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(filt); filt.connect(g); g.connect(dest);
    osc.start(now); osc.stop(now + 0.25);
  }

  tokenDispense(): void {
    // Coin ding — kept identical to former `bloop` so token spawns sound the same.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator(); osc.type = 'sine';
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.2);
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.5, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    osc.connect(g); g.connect(dest);
    osc.start(now); osc.stop(now + 0.5);
  }

  win(): void {
    // bloop + swell layered — preserves current win feel.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;

    // bloop layer
    const bOsc = ctx.createOscillator(); bOsc.type = 'sine';
    bOsc.frequency.setValueAtTime(220, now);
    bOsc.frequency.exponentialRampToValueAtTime(880, now + 0.2);
    const bG = ctx.createGain(); bG.gain.value = 0;
    bG.gain.linearRampToValueAtTime(0.5, now + 0.02);
    bG.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    bOsc.connect(bG); bG.connect(dest);
    bOsc.start(now); bOsc.stop(now + 0.5);

    // swell layer
    const fundamental = 220;
    const ratios = [1, 1.25, 1.5, 1.875];
    const sG = ctx.createGain(); sG.gain.value = 0;
    sG.gain.linearRampToValueAtTime(0.25, now + 0.4);
    sG.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
    sG.connect(dest);
    for (const r of ratios) {
      const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.value = fundamental * r;
      o.detune.value = (Math.random() - 0.5) * 10;
      o.connect(sG); o.start(now); o.stop(now + 1.2);
    }
  }

  death(): void {
    // Mario-style descending wah, ~1.2s. Triangle osc portamento + LFO on gain.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator(); osc.type = 'triangle';
    osc.frequency.setValueAtTime(440, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 1.0);

    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.4, now + 0.04);
    g.gain.setValueAtTime(0.4, now + 1.0);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);

    // LFO wobble for the "wah" feel.
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 8;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 0.18;
    lfo.connect(lfoGain); lfoGain.connect(g.gain);

    osc.connect(g); g.connect(dest);
    osc.start(now); osc.stop(now + 1.25);
    lfo.start(now); lfo.stop(now + 1.25);
  }

  gameOver(): void {
    // Low minor-third sting, ~600ms.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const freqs = [110, 130.81]; // A2 + C3 (minor third)
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.35, now + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
    g.connect(dest);
    for (const f of freqs) {
      const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.value = f;
      o.detune.value = (Math.random() - 0.5) * 6;
      o.connect(g); o.start(now); o.stop(now + 0.65);
    }
  }

  burn(): void {
    // Fire whoosh — filtered white-noise burst.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const bufferSize = Math.floor(ctx.sampleRate * 0.7);
    const buf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const filt = ctx.createBiquadFilter(); filt.type = 'bandpass';
    filt.frequency.setValueAtTime(2000, now);
    filt.frequency.exponentialRampToValueAtTime(300, now + 0.6);
    filt.Q.value = 1.2;
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.32, now + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
    src.connect(filt); filt.connect(g); g.connect(dest);
    src.start(now); src.stop(now + 0.7);
  }

  levelStart(): void {
    // Quick upward ping, ~200ms.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator(); osc.type = 'sine';
    osc.frequency.setValueAtTime(440, now);
    osc.frequency.exponentialRampToValueAtTime(1320, now + 0.18);
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.2, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    osc.connect(g); g.connect(dest);
    osc.start(now); osc.stop(now + 0.22);
  }

  install(): void {
    // Confirmation chime — two stacked sines, ~300ms.
    const ctx = this.ctx; const dest = this.masterGain;
    if (!ctx || !dest) return;
    const now = ctx.currentTime;
    const freqs = [523.25, 783.99]; // C5 + G5
    const g = ctx.createGain(); g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.25, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    g.connect(dest);
    for (const f of freqs) {
      const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = f;
      o.connect(g); o.start(now); o.stop(now + 0.32);
    }
  }
}

export const audio = new LavaAudio();
